import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Sparkles, UserPlus, MessageSquare, Check, ListTodo } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// How often the bell asks, while the tab is in front. Nothing pushes to the
// browser — there is no socket — so this is the whole of "live". Fifteen
// seconds is short enough to feel immediate for people working at the same
// time and cheap enough to ignore: eight users, four requests a minute each.
const POLL_MS = 15000;

// A hidden tab stops asking entirely. Nobody is looking at it, and a laptop
// left open overnight would otherwise make several thousand pointless
// requests before morning.
const ICONS = {
  new_project: Sparkles,
  added_to_project: UserPlus,
  project_update: MessageSquare,
  reminder_due: ListTodo,
};

// Past thirty days the relative form stops meaning anything and a date is
// shown instead. It follows the language chosen in Core: passing undefined
// here hands the decision to the operating system, which is how an Italian
// page ends up writing "Aug 27".
function timeAgo(iso, s, language) {
  if (!iso) return "";
  const then = new Date(iso);
  if (isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return s("feed.justNow");
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return s("feed.yesterday");
  if (days < 30) return `${days}d`;
  return then.toLocaleDateString(language === "it" ? "it-IT" : "en-US", {
    day: "numeric",
    month: "short",
  });
}

/**
 * The bell: what concerns you, as opposed to the home feed, which is what is
 * happening in the company.
 *
 * Every line says why it is there — "added you to", "wrote on" — rather than
 * just naming the project, so the list can be triaged at a glance instead of
 * opened item by item to find out.
 */
export default function NotificationBell() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const s = localStrings(language);

  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    // A slow response must not stack up behind itself when the tab wakes and
    // the timer fires together.
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch {
      // A failing bell should never be visible. Leave the last known state.
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    load();

    let timer = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(load, POLL_MS);
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };

    // Coming back to the tab checks straight away, which is what makes the
    // count look instant: by the time somebody has switched windows and
    // looked up, it is already right.
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        load();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", load);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", load);
    };
  }, [load]);

  // Refresh on open too, so the list is current even between polls.
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const markAll = async () => {
    // Optimistic: the count drops immediately. If the request fails the next
    // poll puts it back, which is the right way round for a courtesy feature.
    setUnread(0);
    setItems(items.map((i) => ({ ...i, read: true })));
    try {
      await axios.post(`${API}/notifications/read`, {}, { withCredentials: true });
    } catch {
      load();
    }
  };

  const openItem = (item) => {
    setOpen(false);
    // Opening the project marks its notifications read server-side, so the
    // count is corrected by the navigation itself. The reminders page does the
    // same for reminder notices.
    if (item.type === "reminder_due") {
      navigate("/promemoria");
      return;
    }
    navigate(`/projects/${item.project_id}`);
  };

  const label = (item) => {
    if (item.type === "reminder_due") return s("notif.reminderDue");
    if (item.type === "new_project") return s("notif.newProject", { name: item.actor_name });
    if (item.type === "added_to_project") return s("notif.addedYou", { name: item.actor_name });
    if ((item.count || 1) > 1) return s("notif.updatesMany", { count: item.count });
    return s("notif.updateOne", { name: item.actor_name });
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          aria-label={s("notif.title")}
          className="relative p-2 rounded-lg text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              data-testid="notification-count"
              className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            {s("notif.title")}
          </span>
          {unread > 0 && (
            <button
              onClick={markAll}
              data-testid="notification-mark-all"
              className="text-xs font-medium text-indigo-600 hover:underline inline-flex items-center gap-1"
            >
              <Check className="w-3 h-3" />
              {s("notif.markAll")}
            </button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8 px-4" data-testid="notification-empty">
              {s("notif.empty")}
            </p>
          ) : (
            items.map((item) => {
              const Icon = ICONS[item.type] || MessageSquare;
              return (
                <button
                  key={item.id}
                  onClick={() => openItem(item)}
                  data-testid={`notification-${item.id}`}
                  className={`w-full text-left px-3 py-2.5 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors flex items-start gap-2.5 ${
                    item.read ? "opacity-55" : ""
                  }`}
                >
                  <span
                    className="w-6 h-6 rounded-full shrink-0 mt-0.5 flex items-center justify-center"
                    style={{ backgroundColor: item.read ? "#e2e8f0" : (item.actor_color || "#3730A3") }}
                  >
                    <Icon className={`w-3 h-3 ${item.read ? "text-slate-500" : "text-white"}`} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-slate-800 leading-snug">
                      {label(item)}
                    </span>
                    <span className="block text-xs text-slate-500 truncate">
                      {item.project_title}
                    </span>
                  </span>
                  <span className="text-[11px] text-slate-400 shrink-0 mt-0.5">
                    {timeAgo(item.created_at, s, language)}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
